/**
 * Shared composable for staging and persisting group member assignments.
 *
 * Both GroupManagement and UserGroupAssignment use the same underlying
 * logic: load members into a working copy, add/remove locally, and
 * flush to the server in a single PUT on Save.  The two screens differ
 * only in their selection model (single vs multi-select) and display
 * format — everything else lives here.
 */
import { ref, computed, type Ref } from 'vue';
import type { UserProfile } from '@/models/TableMaintenanceViewModels';
import type { useTableMaintenanceApi } from '@/composables/UseTableMaintenanceApi';
import type { Tell } from '@/services/Tell';

type TableMaintenanceApi = ReturnType<typeof useTableMaintenanceApi>;

export function useGroupMemberAssignment(
	appContext: Ref<string>,
	api: TableMaintenanceApi,
	tell: Tell,
) {
	/** All users returned from the server. */
	const allUsers = ref<UserProfile[]>([]);

	/** Working copy — staged locally until the user clicks Save. */
	const assignedUsers = ref<UserProfile[]>([]);

	/** Snapshot of the ids that were last committed to the server. */
	const originalAssignedIds = ref<Set<string>>(new Set());

	/** Normalize a userId for consistent comparison (trim + lowercase). */
	function normalizeId(id: string): string {
		return id.trim().toLowerCase();
	}

	/**
	 * Build a unique key for a user row. Uses the normalized userId first,
	 * then falls back to name-based matching so rows that represent the
	 * same person but carry slightly different userId strings are merged.
	 */
	function userKey(u: UserProfile): string {
		const nameKey = `${(u.lastName ?? '').trim().toLowerCase()}|${(u.firstName ?? '').trim().toLowerCase()}`;
		const idKey = normalizeId(u.userId);
		// If both name parts are present, use name as key to catch variants;
		// otherwise fall back to userId.
		return nameKey !== '|' ? nameKey : idKey;
	}

	/** Users not in the working copy, derived reactively and deduplicated. */
	const availableUsers = computed(() => {
		const assignedIds = new Set(assignedUsers.value.map(u => normalizeId(u.userId)));
		const assignedNames = new Set(assignedUsers.value.map(u => userKey(u)));
		const filtered = allUsers.value.filter(u =>
			!assignedIds.has(normalizeId(u.userId)) && !assignedNames.has(userKey(u))
		);

		// Deduplicate: merge multiple rows for the same user into one,
		// combining groupId values into a comma-separated string.
		const userMap = new Map<string, UserProfile>();
		for (const u of filtered) {
			const key = userKey(u);
			const existing = userMap.get(key);
			if (existing) {
				if (u.groupId && !existing.groupId.split(', ').includes(u.groupId)) {
					existing.groupId = existing.groupId ? `${existing.groupId}, ${u.groupId}` : u.groupId;
				}
			} else {
				userMap.set(key, { ...u, userId: u.userId.trim() });
			}
		}
		return Array.from(userMap.values());
	});

	/** True when the working copy differs from the last-saved server state. */
	const hasChanges = computed(() => {
		const currentIds = new Set(assignedUsers.value.map(u => normalizeId(u.userId)));
		if (currentIds.size !== originalAssignedIds.value.size) return true;
		for (const id of currentIds) {
			if (!originalAssignedIds.value.has(id)) return true;
		}
		return false;
	});

	// ── Data loading ────────────────────────────────────────────────────

	async function loadAllUsers(): Promise<void> {
		try {
			allUsers.value = await api.getAllUsers(appContext.value);
		} catch (e: unknown) {
			tell.error(`Error loading users: ${(e as Error).message}`);
		}
	}

	async function loadGroupMembers(groupId: string): Promise<void> {
		try {
			const members = await api.getGroupUsers(groupId, appContext.value);
			// Deduplicate: keep only one row per user.
			const seen = new Map<string, UserProfile>();
			for (const m of members) {
				const key = userKey(m);
				if (!seen.has(key)) seen.set(key, { ...m, userId: m.userId.trim() });
			}
			const unique = Array.from(seen.values());
			assignedUsers.value = unique;
			originalAssignedIds.value = new Set(unique.map(m => normalizeId(m.userId)));
		} catch (e: unknown) {
			tell.error(`Error loading group members: ${(e as Error).message}`);
		}
	}

	/** Reset the working copy to empty (call when no group is selected). */
	function clearMembers(): void {
		assignedUsers.value = [];
		originalAssignedIds.value = new Set();
	}

	// ── Staged transfer operations ───────────────────────────────────────

	function addToAssigned(users: UserProfile[]): void {
		const existingKeys = new Set(assignedUsers.value.map(u => userKey(u)));
		const newUsers = users.filter(u => !existingKeys.has(userKey(u)));
		assignedUsers.value = [...assignedUsers.value, ...newUsers];
	}

	function removeFromAssigned(userIds: string[]): void {
		const toRemove = new Set(userIds.map(id => normalizeId(id)));
		assignedUsers.value = assignedUsers.value.filter(u => !toRemove.has(normalizeId(u.userId)));
	}

	// ── Save / Reset ─────────────────────────────────────────────────────

	/**
	 * Persist the current working copy to the server via a single PUT.
	 * Handles success/error notifications.  Returns true on success.
	 */
	async function saveMembers(groupId: string, user: string): Promise<boolean> {
		const rawUserIds = assignedUsers.value.map(u => u.userId);
		const trimmedUserIds = rawUserIds.map(id => id.trim());
		const normalizedUserIds = trimmedUserIds.map(id => normalizeId(id));
		try {
			const result = await api.updateGroupMembers({
				user,
				appContext: appContext.value,
				groupName: groupId,
				userIds: trimmedUserIds,
			});
			if (result.success) {
				tell.success(result.message);
				originalAssignedIds.value = new Set(normalizedUserIds);
				// Refresh all-users so groupId fields reflect the new assignments.
				await loadAllUsers();
				return true;
			}
			tell.error(result.message);
			return false;
		} catch (e: unknown) {
			tell.error(`Error saving changes: ${(e as Error).message}`);
			return false;
		}
	}

	/** Discard working-copy changes and restore the last-saved state. */
	function resetMembers(): void {
		const originalIds = originalAssignedIds.value;
		const seenKeys = new Set<string>();
		assignedUsers.value = allUsers.value.filter(u => {
			const normalizedId = normalizeId(u.userId);
			if (!originalIds.has(normalizedId)) {
				return false;
			}
			const key = userKey(u);
			if (seenKeys.has(key)) {
				return false;
			}
			seenKeys.add(key);
			return true;
		});
	}

	return {
		allUsers, assignedUsers, availableUsers, hasChanges,
		loadAllUsers, loadGroupMembers, clearMembers,
		addToAssigned, removeFromAssigned,
		saveMembers, resetMembers,
	};
}
